/**
 * Admin form validators — the admin-side analog of
 * `web/src/components/inquiry/validators.ts`. One `validate*` function per form,
 * each returning a per-field `FieldErrors` map so the UI can surface every
 * field's error at once (no single-error early-return) and clear each message
 * live as its field becomes valid.
 *
 * The generic contract — `checkField`, `Rule`, `FieldErrors` — lives in
 * `@shared/validators` so web and admin share one copy. Primitives
 * (`isValidEmail`, `isValidPhone`, …) come from there too. Step/form-level shape
 * rules (cross-field, conditional requireds) live here.
 */
import {
  collectFieldErrors,
  emailRule,
  isInRange,
  isPositiveInt,
  phoneRule,
  withinMaxLength,
  type FieldErrors,
  type FieldSpec,
  type Rule,
} from "@shared/validators";
import { isAvailabilityControlledItem } from "../../lib/booking-item-types";

export type ContactField = "name" | "email" | "phone";

/**
 * Shared name/email/phone validator for the Contractor modal, Supplier modal,
 * and the inline supplier-create combobox. `email`/`phone` are OPTIONAL params:
 * pass them only for forms that collect them, so the name-only combobox doesn't
 * carry phantom email/phone keys. Both are optional fields (not required) — a
 * blank email/phone is valid; only a non-blank malformed value errors.
 */
export function validateContactFields(fields: {
  name: string;
  email?: string;
  phone?: string;
}): FieldErrors<ContactField> {
  const specs: Partial<Record<ContactField, FieldSpec>> = {
    name: {
      value: fields.name,
      rule: { required: true, emptyMsg: "Name is required" },
    },
  };
  // Only collect email/phone for forms that pass them, so the name-only
  // combobox never carries phantom keys (cross-field presence stays here).
  if (fields.email !== undefined) {
    specs.email = { value: fields.email, rule: emailRule };
  }
  if (fields.phone !== undefined) {
    specs.phone = { value: fields.phone, rule: phoneRule };
  }
  return collectFieldErrors(specs);
}

// ── Equipment catalog item ──────────────────────────────────────────────────
//
// `hire_rate` and `stock_quantity` are PLAIN TEXT inputs (no clamp), so per the
// plan's A2 they route through `checkField` with distinct empty-vs-invalid
// messages — NOT the clamp-and-announce NumberInput path. Each field keeps its
// own accepted range: rate ≥ 0 (free is valid), quantity ≥ 1 integer. The
// list-route inline rows and this create/edit form share these rules so
// "valid rate"/"valid quantity" mean the same thing in both places.

export type EquipmentFormField =
  | "name"
  | "hire_rate"
  | "stock_quantity"
  | "weight_kg"
  | "length_mm"
  | "width_mm"
  | "height_mm"
  | "insurable_value";

export const EQUIPMENT_NAME_MAX = 256;
export const EQUIPMENT_NAME_TOO_LONG_MSG = `Keep this under ${EQUIPMENT_NAME_MAX} characters`;
export const RATE_INVALID_MSG = "Enter a valid rate (0 or more)";
export const RATE_EMPTY_MSG = "Hire rate is required";
export const NAME_EMPTY_MSG = "Name is required";
export const QTY_EMPTY_MSG = "Stock quantity is required";
export const QTY_INVALID_MSG = "Enter a valid quantity (1 or more)";

/** Hire rate: a finite number ≥ 0 (free hire is legitimate). */
export const isValidRate = (v: string): boolean =>
  isInRange(v, 0, Number.MAX_SAFE_INTEGER);

// ── One rule definition per equipment field ─────────────────────────────────
//
// Each field's format check (predicate + invalidMsg) is declared exactly ONCE
// below. The inline autosave rows consume these format-only rules directly (the
// `useEditableField` hook can't enforce `required` — a cleared cell falls back
// to 0 — so it only surfaces *malformed* messages). The create/edit modal needs
// the same format check PLUS `required`, so it layers required-ness on via
// `required(...)` rather than re-typing the predicate and message. One source of
// truth: change a message or bound here and both paths move together.

/** The format-only rate rule (malformed-only; a blank rate means $0). */
export const rateRule = {
  predicate: isValidRate,
  invalidMsg: RATE_INVALID_MSG,
};

/** The format-only quantity rule (malformed-only). */
export const qtyFormatRule = {
  predicate: isPositiveInt,
  invalidMsg: QTY_INVALID_MSG,
};

/** The format-only name rule — length-capped (malformed-only). */
export const nameLengthRule = {
  predicate: (v: string) => withinMaxLength(v, EQUIPMENT_NAME_MAX),
  invalidMsg: EQUIPMENT_NAME_TOO_LONG_MSG,
};

// ── Physical facts (#189) — OPTIONAL fields, format-only ────────────────────
//
// Blank is valid and persists as null (= unknown, never zero), so these rules
// never carry `required`; only a non-blank malformed value errors.

export const NON_NEG_NUMBER_MSG = "Enter a number (0 or more)";
export const NON_NEG_INT_MSG = "Enter a whole number (0 or more)";

/** Format-only: blank OK, else a finite number ≥ 0. */
export const nonNegNumberRule = {
  predicate: (v: string) =>
    v.trim() === "" || isInRange(v, 0, Number.MAX_SAFE_INTEGER),
  invalidMsg: NON_NEG_NUMBER_MSG,
};

/** Format-only: blank OK, else a whole number ≥ 0. */
export const nonNegIntRule = {
  predicate: (v: string) => {
    const t = v.trim();
    if (t === "") return true;
    const n = Number(t);
    return Number.isInteger(n) && n >= 0;
  },
  invalidMsg: NON_NEG_INT_MSG,
};

/**
 * Layer `required` + an empty-message onto a format-only rule. The single
 * modifier that turns an inline-row rule into the stricter modal rule, so the
 * "modal is stricter" distinction is one property added to one rule rather than
 * a second hand-written copy of the predicate/message.
 */
function required(
  base: { predicate: (v: string) => boolean; invalidMsg: string },
  emptyMsg: string,
): Rule {
  return { required: true, emptyMsg, ...base };
}

/**
 * Create/edit form validator. Surfaces every field at once. Each field is the
 * same format rule the inline rows use, with `required` layered on: `name` is
 * required AND length-capped; `hire_rate` is required AND must be ≥ 0 — the
 * operator must enter a number even for a free hire ($0), so a blank rate can't
 * silently save as 0 (distinct empty vs invalid); `stock_quantity` is required
 * AND must be ≥ 1.
 */
export function validateEquipment(fields: {
  name: string;
  rate: string;
  qty: string;
  weight?: string;
  length?: string;
  width?: string;
  height?: string;
  insurableValue?: string;
}): FieldErrors<EquipmentFormField> {
  const specs: Partial<Record<EquipmentFormField, FieldSpec>> = {
    name: {
      value: fields.name,
      rule: required(nameLengthRule, NAME_EMPTY_MSG),
    },
    hire_rate: { value: fields.rate, rule: required(rateRule, RATE_EMPTY_MSG) },
    stock_quantity: {
      value: fields.qty,
      rule: required(qtyFormatRule, QTY_EMPTY_MSG),
    },
  };
  // Physical facts are optional — only collected when the form passes them,
  // and format-only (blank = unknown is always valid).
  if (fields.weight !== undefined) {
    specs.weight_kg = { value: fields.weight, rule: nonNegNumberRule };
  }
  if (fields.length !== undefined) {
    specs.length_mm = { value: fields.length, rule: nonNegIntRule };
  }
  if (fields.width !== undefined) {
    specs.width_mm = { value: fields.width, rule: nonNegIntRule };
  }
  if (fields.height !== undefined) {
    specs.height_mm = { value: fields.height, rule: nonNegIntRule };
  }
  if (fields.insurableValue !== undefined) {
    specs.insurable_value = {
      value: fields.insurableValue,
      rule: nonNegNumberRule,
    };
  }
  return collectFieldErrors<EquipmentFormField>(specs);
}

// ── Job details (vehicle + conditional driver) ──────────────────────────────
//
// The "Generate Job Summary" action gates on a vehicle being chosen, and — when
// the chosen vehicle is the contractor's PERSONAL vehicle — on a driver being
// selected (a cross-field, conditional requirement: driver only matters once
// `vehicleType === "personal"`). Both are `<select>` fields whose empty value is
// the literal empty string, so `checkField`'s required-empty path fits exactly.

export type JobDetailsField = "vehicleType" | "driver";

export const VEHICLE_REQUIRED_MSG = "Select a vehicle option";
export const DRIVER_REQUIRED_MSG = "Select a driver for the personal vehicle";

/**
 * Whole-form validator for the job-details vehicle block. `vehicleType` is
 * always required; `driver` (the assigned contractor id) is required ONLY when
 * the vehicle type is "personal". Returns a `FieldErrors` map so both fields can
 * surface at once and each clears live as it becomes valid.
 */
export function validateJobDetails(fields: {
  vehicleType: string;
  driver: string;
}): FieldErrors<JobDetailsField> {
  const specs: Partial<Record<JobDetailsField, FieldSpec>> = {
    vehicleType: {
      value: fields.vehicleType,
      rule: { required: true, emptyMsg: VEHICLE_REQUIRED_MSG },
    },
  };
  // Cross-field: driver is required only once the personal vehicle is chosen.
  if (fields.vehicleType === "personal") {
    specs.driver = {
      value: fields.driver,
      rule: { required: true, emptyMsg: DRIVER_REQUIRED_MSG },
    };
  }
  return collectFieldErrors(specs);
}

// ── Gear list (per-row item name) ───────────────────────────────────────────
//
// Generating a quote requires every gear row to carry a non-blank item name.
// The map is keyed by the row's stable identity (clientKey ?? id ?? index) so
// each row surfaces its own message and clears live as it's filled in.

export const GEAR_ITEM_NAME_REQUIRED_MSG = "Item name required";
export const GEAR_ITEM_NOT_IN_CATALOG_MSG = "Select an item from the catalogue";

/**
 * Validates a list of gear rows, returning a map keyed by each row's identity
 * with the error string for any invalid row. An empty map means every row is
 * valid. The caller supplies the key for each row so the map lines up with
 * however the rows are rendered/keyed.
 *
 * Two rules, in order of precedence:
 *   1. item_name must be non-blank.
 *   2. gear-type rows must resolve to a real catalogue entry (equipment_id set).
 *      Free-typed names with no autocomplete match leave equipment_id null and
 *      are rejected (#364) — quotes may only list real inventory. Non-gear rows
 *      (tech-time / vehicle) are system-derived and exempt.
 */
// ── Quote packages (#20) ────────────────────────────────────────────────────
//
// Mirror of the server rules (validateQuotePackages in
// go-service/internal/api/gear_items.go + the per-package emptiness 400 at
// generation): at most 3 packages, unique non-empty names, every row inside a
// declared package, and no empty packages. Generation 400s on the same rules
// — this surfaces them before the request.

export const MAX_QUOTE_PACKAGES_MSG = "A quote can offer at most 3 packages";

export function validateQuotePackages(
  packages: { key: string; name: string }[],
  rows: { package_key: string | null }[],
): string[] {
  const errors: string[] = [];
  if (packages.length > 3) errors.push(MAX_QUOTE_PACKAGES_MSG);
  const names = new Set<string>();
  const keys = new Set<string>();
  for (const p of packages) {
    keys.add(p.key);
    const name = p.name.trim().toLowerCase();
    if (!name) {
      errors.push("Each package needs a name");
      continue;
    }
    if (names.has(name))
      errors.push(`Package names must be unique ("${p.name.trim()}")`);
    names.add(name);
  }
  if (packages.length > 0) {
    if (rows.some((r) => !r.package_key || !keys.has(r.package_key))) {
      errors.push("Every gear row must belong to one of the quote packages");
    }
    for (const p of packages) {
      if (!rows.some((r) => r.package_key === p.key)) {
        errors.push(
          `Package "${p.name.trim() || "Unnamed"}" has no gear lines`,
        );
      }
    }
  } else if (rows.some((r) => r.package_key)) {
    errors.push("Gear rows cannot reference a package when the quote has none");
  }
  return [...new Set(errors)];
}

export function validateGearList(
  rows: {
    key: string;
    item_name: string;
    type?: string | null;
    equipment_id?: string | null;
  }[],
): FieldErrors<string> {
  const errors: FieldErrors<string> = {};
  for (const row of rows) {
    const specs: Partial<Record<string, FieldSpec>> = {
      [row.key]: {
        value: row.item_name,
        rule: { required: true, emptyMsg: GEAR_ITEM_NAME_REQUIRED_MSG },
      },
    };
    const blankErr = collectFieldErrors(specs)[row.key];
    if (blankErr) {
      errors[row.key] = blankErr;
      continue;
    }
    if (isAvailabilityControlledItem(row.type) && !row.equipment_id) {
      errors[row.key] = GEAR_ITEM_NOT_IN_CATALOG_MSG;
    }
  }
  return errors;
}
